import React, { useEffect, useMemo, useState } from 'react';
import { Table, Form } from 'react-bootstrap';
import { useInstrumentPoolingDisplay, saveInstrumentPoolingDisplay } from '../util/db';
import { calculatePooledValues, computeInstrumentLimitsStats } from '../util/instrumentStats';
import {
  buildPoolingDisplayKey,
  formatRsd,
  resolvePoolingSelections
} from '../util/instrumentPoolingDisplay';

// Display-only pooling table shown below the Instrument Limits table: n, mean,
// SD and RSD per instrument plus pooled rows, with its own persisted
// "Selection for Pooling" that is independent of the table above.
const InstrumentPoolingDisplay = ({ data, location, method, sampleName, parameter }) => {
  const key = buildPoolingDisplayKey({ location, method, sampleName, parameter });
  const { data: saved } = useInstrumentPoolingDisplay(key);
  // Edits made in this session, layered over the saved selections so the table
  // updates immediately without waiting for the Firestore round trip.
  const [localEdits, setLocalEdits] = useState({});

  useEffect(() => {
    setLocalEdits({});
  }, [key]);

  const { instrumentStats } = useMemo(
    () => computeInstrumentLimitsStats({ data, method, sampleName, location, parameter }),
    [data, method, sampleName, location, parameter]
  );

  const savedSelections = saved?.selections;
  const selections = useMemo(
    () =>
      resolvePoolingSelections(
        instrumentStats.map((stat) => stat.instrument),
        { ...savedSelections, ...localEdits }
      ),
    [instrumentStats, savedSelections, localEdits]
  );

  const pooledValuesAll = calculatePooledValues(instrumentStats, false, selections);
  const pooledValuesSelected = calculatePooledValues(instrumentStats, true, selections);

  const handleSelectionChange = (instrument, value) => {
    setLocalEdits((prev) => ({ ...prev, [instrument]: value }));
    saveInstrumentPoolingDisplay(key, instrument, value).catch((error) => {
      console.error('Error saving instrument pooling selections', error);
    });
  };

  const sortedStats = [...instrumentStats].sort((a, b) =>
    a.instrument.localeCompare(b.instrument, undefined, { numeric: true })
  );

  const pooledRow = (label, className, values) =>
    values && (
      <tr className={className}>
        <td>
          <strong>{label}</strong>
        </td>
        <td>{values.n}</td>
        <td>{values.mean}</td>
        <td>{values.stdDev}</td>
        <td>{formatRsd(values.mean, values.stdDev)}</td>
        <td>-</td>
      </tr>
    );

  return (
    <div className="instrument-pooling-display-table-container mt-4">
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Instrument</th>
            <th>N</th>
            <th>Mean</th>
            <th>Standard Deviation</th>
            <th>RSD</th>
            <th>Selection for Pooling</th>
          </tr>
        </thead>
        <tbody>
          {sortedStats.length > 0 ? (
            <>
              {sortedStats.map((stat) => (
                <tr key={`pooling-display-${stat.instrument}`}>
                  <td>{stat.instrument}</td>
                  <td>{stat.n}</td>
                  <td>{stat.mean}</td>
                  <td>{stat.stdDev}</td>
                  <td>{formatRsd(stat.rawMean, stat.rawStdDev)}</td>
                  <td>
                    <Form.Select
                      value={selections[stat.instrument] || 'Y'}
                      onChange={(e) => handleSelectionChange(stat.instrument, e.target.value)}
                      aria-label="Selection for Pooling"
                    >
                      <option value="Y">Y</option>
                      <option value="N">N</option>
                    </Form.Select>
                  </td>
                </tr>
              ))}

              <tr className="pooled-values-separator">
                <td colSpan="6" style={{ padding: '0.5rem', backgroundColor: '#f0f0f0' }}></td>
              </tr>
              {pooledRow('Pooled Values (all)', 'pooled-values-all', pooledValuesAll)}
              {pooledRow(
                'Pooled Values (selected)',
                'pooled-values-selected',
                pooledValuesSelected
              )}
            </>
          ) : (
            <tr>
              <td colSpan="6" className="text-center">
                No instrument data available for the selected method and sample name
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default InstrumentPoolingDisplay;
